// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Coming soon previews  (route /coming-soon — the list of previews, linked from the profile page)
// Lists every preview in data/comingSoon.ts with what it will extend. Each opens pages/ComingSoon. Previews with a `live` route link
// straight to the real screen. Open to everyone; not in the sitemap.
// ─────────────────────────────────────────────────────────────────────────────
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronRight, Hourglass } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import PageTransition from "../components/PageTransition";
import { PREVIEWS } from "../data/comingSoon";
import useDocumentTitle from "../hooks/useDocumentTitle";

export default function ComingSoonIndex() {
  useDocumentTitle("Coming soon");
  return (
    <PageTransition>
      <div className="bg-foam-50 pb-24 pt-28 dark:bg-ocean-950 sm:pt-32">
        <Breadcrumbs />
        <div className="mx-auto flex max-w-xl flex-col gap-4 px-4 pt-2">
          <header className="flex items-center gap-3">
            <Link to="/profile" aria-label="Back to my profile" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-ocean-900 shadow-sm ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 dark:bg-ocean-900 dark:text-foam-100 dark:ring-white/10">
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </Link>
            <div className="min-w-0 flex-1">
              <h1 className="font-display text-2xl font-bold text-ocean-950 dark:text-foam-50">Coming soon</h1>
              <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">What we&apos;re building next</p>
            </div>
          </header>
          <p className="flex items-start gap-2 rounded-2xl bg-coral-500/10 p-4 text-sm text-ocean-800/90 ring-1 ring-coral-500/30 dark:text-foam-100/85">
            <Hourglass className="mt-0.5 h-5 w-5 shrink-0 text-coral-600 dark:text-coral-300" aria-hidden="true" />
            These are previews. Nothing in them can be used or ordered yet, and the figures shown are samples.
          </p>
          <ul className="flex flex-col gap-2">
            {PREVIEWS.map((preview) => (
              <li key={preview.slug}>
                <Link to={preview.live ?? `/coming-soon/${preview.slug}`} className="flex min-h-16 items-center gap-3 rounded-2xl bg-white p-3 shadow-sm ring-1 ring-ocean-900/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 dark:bg-ocean-900 dark:ring-white/10">
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="text-sm font-bold text-ocean-950 dark:text-foam-50">{preview.title}</span>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${preview.live ? "bg-teal-100 text-teal-700" : "bg-coral-500/10 text-coral-600 dark:text-coral-300"}`}>{preview.live ? "Ready" : "Soon"}</span>
                    </span>
                    <span className="block text-xs text-ocean-800/80 dark:text-foam-100/75">{preview.summary}</span>
                    <span className="block text-[11px] text-ocean-800/70 dark:text-foam-100/65">Part of {preview.parent.label}</span>
                  </span>
                  <ChevronRight className="h-5 w-5 shrink-0 text-ocean-800/60 dark:text-foam-100/60" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </PageTransition>
  );
}
